import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { getProjects } from "../../services/projectService";
import { getUsers } from "../../services/userService";
import { addTask } from "../../services/taskService";
import { formatDate } from "../../utils/formatDate";

const CreateTask = () => {
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [files, setFiles] = useState([]); // ✅ ADDED

  const [searchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get("projectId");

  const [formData, setFormData] = useState({
    project_id: "",
    name: "",
    description: "",
    assigned_to: "",
    priority: "Medium",
    status: "Pending",
    deadline: formatDate(new Date()),
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ================= LOAD DATA ================= */
  const goBack = () => navigate(-1);

  useEffect(() => {
    async function loadData() {
      try {
        const [projectsRes, usersRes] = await Promise.all([
          getProjects(),
          getUsers(),
        ]);
        setProjects(projectsRes);
        setUsers(usersRes);
      } catch (err) {
        console.error("Failed to load initial data", err);
      }
    }
    loadData();
  }, []);

  /* ================= AUTO SELECT PROJECT ================= */
  useEffect(() => {
    if (projectIdFromUrl) {
      setFormData((prev) => ({
        ...prev,
        project_id: projectIdFromUrl,
      }));
    }
  }, [projectIdFromUrl]);

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      /* ===== FORM DATA (LIKE CREATE BUG) ===== */
      const payload = new FormData();
      payload.append("project_id", formData.project_id);
      payload.append("name", formData.name);
      payload.append("description", formData.description);
      payload.append("assigned_to", formData.assigned_to);
      payload.append("priority", formData.priority);
      payload.append("status", formData.status);
      payload.append("deadline", formData.deadline);
      payload.append("action", "add");

      
      for (let i = 0; i < files.length; i++) {
        payload.append("attachments[]", files[i]);
      }

      let res = await addTask(payload);
      console.log(res);
      
      alert("Task created successfully!");
      goBack();
    } catch (err) {
      console.error("Create task failed:", err);
      setError("Failed to create task. Try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */
  return (
    <>
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Create New Task</h1>
          <p className="text-sm text-gray-500">
            Assign a task to a project and team member
          </p>
        </div>

        <Link
          onClick={goBack}
          className="text-sm text-blue-600 hover:underline"
        >
          ← Back
        </Link>
      </div>

      {/* FORM */}
      <div className="bg-white rounded-xl shadow-sm p-6 max-w-3xl">
        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Project */}
          <div>
            <label className="block font-medium mb-1">Project</label>
            <select
              name="project_id"
              value={formData.project_id}
              onChange={handleChange}
              required
              className="w-full border px-3 py-2 rounded"
            >
              <option value="">Select Project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="block font-medium mb-1">Task Title</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full border px-3 py-2 rounded"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block font-medium mb-1">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows="4"
              className="w-full border px-3 py-2 rounded"
            />
          </div>

          {/* ✅ ATTACHMENTS (ADDED) */}
          <div>
            <label className="block font-medium mb-1">Attachments</label>
            <input
              type="file"
              multiple
              onChange={(e) => setFiles(e.target.files)}
              className="w-full border px-3 py-2 rounded"
            />
            <p className="text-xs text-gray-500 mt-1">
              Upload screenshots, documents, or files
            </p>
          </div>

          {/* Assign To */}
          <div>
            <label className="block font-medium mb-1">Assign To</label>
            <select
              name="assigned_to"
              value={formData.assigned_to}
              onChange={handleChange}
              required
              className="w-full border px-3 py-2 rounded"
            >
              <option value="">Select User</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          {/* Priority & Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-medium mb-1">Priority</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label className="block font-medium mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded"
              >
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="on-hold">On Hold</option>
              </select>
            </div>
          </div>

          {/* Due Date */}
          <div>
            <label className="block font-medium mb-1">Due Date</label>
            <input
              type="date"
              name="deadline"
              value={formData.deadline}
              onChange={handleChange}
              required
              className="w-full border px-3 py-2 rounded"
            />
          </div>

          {/* SUBMIT */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition"
          >
            {loading ? "Saving..." : "Create Task"}
          </button>
        </form>
      </div>
    </>
  );
};

export default CreateTask;
